import React, { useState, useRef } from 'react';

const API = 'https://ster-ai-production.up.railway.app';

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [etape, setEtape] = useState('identifiants');
  const [erreur, setErreur] = useState('');
  const [loading, setLoading] = useState(false);
  const [donneesEnAttente, setDonneesEnAttente] = useState(null);
  const [cguDefile, setCguDefile] = useState(false);
  const cguRef = useRef(null);
  const [contratStructure, setContratStructure] = useState(null);
  const [contratDefile, setContratDefile] = useState(false);
  const contratRef = useRef(null);
  const [theme, setTheme] = useState(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    return 'light';
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErreur('');

    try {
      const res = await fetch(`${API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, mot_de_passe: password })
      });

      if (res.ok) {
        setEtape('code');
      } else {
        setErreur('Email ou mot de passe incorrect');
      }
    } catch {
      setErreur('Impossible de contacter le serveur');
    }

    setLoading(false);
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErreur('');

    try {
      const res = await fetch(`${API}/login/verifier-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });

      if (res.ok) {
        const data = await res.json();
        const utilisateur = {
          email: data.email,
          nom: data.nom,
          client_id: data.client_id,
          role: data.role,
          nom_client: data.nom_client,
          has_calendrier: data.has_calendrier,
          onboarding_complete: data.onboarding_complete,
          contrat_a_accepter: data.contrat_a_accepter,
          token: data.token
        };
        if (!data.cgu_acceptees) {
          setDonneesEnAttente(utilisateur);
          setEtape('cgu');
        } else if (data.contrat_a_accepter) {
          setDonneesEnAttente(utilisateur);
          await chargerContrat(utilisateur.token);
        } else {
          onLogin(utilisateur);
        }
      } else {
        setErreur('Code incorrect ou expiré');
      }
    } catch {
      setErreur('Impossible de contacter le serveur');
    }

    setLoading(false);
  };

  const handleScrollCgu = () => {
    const el = cguRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setCguDefile(true);
  };

  const handleAccepterCgu = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/accepter-cgu`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${donneesEnAttente.token}` }
      });
    } catch {}
    if (donneesEnAttente.contrat_a_accepter) {
      await chargerContrat(donneesEnAttente.token);
    } else {
      onLogin(donneesEnAttente);
    }
    setLoading(false);
  };

  const chargerContrat = async (token) => {
    try {
      const res = await fetch(`${API}/contrat`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (data.existe && !data.accepte) {
          setContratStructure(data.structure);
          setEtape('contrat');
          return;
        }
      }
    } catch {}
    onLogin(donneesEnAttente);
  };

  const handleScrollContrat = () => {
    const el = contratRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setContratDefile(true);
  };

  const handleAccepterContrat = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/contrat/accepter`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${donneesEnAttente.token}` }
      });
    } catch {}
    onLogin(donneesEnAttente);
    setLoading(false);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,600;12..96,700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');

        .login-page[data-login-theme="light"] {
          --lg-bg: #fbfaf9; --lg-bg-alt: #f2f0ec; --lg-surface: #ffffff;
          --lg-surface-border: rgba(28,27,40,0.08); --lg-border: rgba(28,27,40,0.1);
          --lg-text: #1c1b28; --lg-text-muted: #6b6975; --lg-text-faint: #9997a3;
          --lg-brand: #5E6AD2; --lg-brand-deep: #3730A3; --lg-brand-soft: rgba(94,106,210,0.08);
          --lg-error-bg: #fbeeed; --lg-error-text: #B3392F; --lg-error-border: rgba(179,57,47,0.25);
        }
        .login-page[data-login-theme="dark"] {
          --lg-bg: #1c1b28; --lg-bg-alt: #221f30; --lg-surface: #26243a;
          --lg-surface-border: rgba(251,250,249,0.08); --lg-border: rgba(251,250,249,0.12);
          --lg-text: #f5f4f2; --lg-text-muted: #9c9aab; --lg-text-faint: #6f6d80;
          --lg-brand: #7C87E8; --lg-brand-deep: #3730A3; --lg-brand-soft: rgba(124,135,232,0.16);
          --lg-error-bg: rgba(229,114,107,0.12); --lg-error-text: #E5726B; --lg-error-border: rgba(229,114,107,0.3);
        }

        * { box-sizing: border-box; }

        .login-page {
          min-height: 100vh;
          background: var(--lg-bg);
          color: var(--lg-text);
          display: flex;
          flex-direction: column;
          font-family: 'IBM Plex Sans', sans-serif;
          -webkit-font-smoothing: antialiased;
          transition: background 0.3s, color 0.3s;
        }

        .login-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px 32px;
        }

        .login-nav-link {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--lg-text-muted);
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
        }

        .login-theme-toggle {
          width: 36px; height: 36px;
          border-radius: 10px;
          border: 1px solid var(--lg-border);
          background: transparent;
          color: var(--lg-text-muted);
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: border-color 0.15s, color 0.15s;
        }
        .login-theme-toggle:hover { color: var(--lg-text); border-color: var(--lg-text-faint); }

        .login-main {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px 32px 64px;
        }

        .login-card-wrap { width: 100%; max-width: 400px; }

        .login-logo-center {
          display: flex; align-items: center; justify-content: center;
          gap: 8px; margin-bottom: 32px;
        }
        .login-logo-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--lg-brand); }
        .login-logo-text { font-family: 'Bricolage Grotesque', sans-serif; font-weight: 600; font-size: 19px; letter-spacing: -0.01em; }

        .login-card {
          background: var(--lg-surface);
          border: 1px solid var(--lg-surface-border);
          border-radius: 18px;
          padding: 36px;
          box-shadow: 0 24px 60px -30px rgba(28,27,40,0.22);
        }

        .login-title {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: 22px; font-weight: 600; letter-spacing: -0.01em;
          margin: 0 0 6px;
        }
        .login-sub { font-size: 14.5px; color: var(--lg-text-muted); margin: 0 0 28px; }

        .login-field { margin-bottom: 18px; }
        .login-field:last-of-type { margin-bottom: 24px; }
        .login-label {
          display: block; font-size: 13px; font-weight: 600;
          color: var(--lg-text-muted); margin-bottom: 7px;
        }
        .login-input {
          width: 100%; padding: 12px 14px; font-size: 15px;
          font-family: 'IBM Plex Sans', sans-serif; color: var(--lg-text);
          background: var(--lg-bg); border: 1px solid var(--lg-border);
          border-radius: 10px; outline: none; transition: border-color 0.15s, box-shadow 0.15s;
        }
        .login-input:focus { border-color: var(--lg-brand); box-shadow: 0 0 0 3px var(--lg-brand-soft); }
        .login-input::placeholder { color: var(--lg-text-faint); }

        .login-erreur {
          display: flex; align-items: center; gap: 8px;
          font-size: 13px; color: var(--lg-error-text);
          margin-bottom: 18px; padding: 10px 14px;
          background: var(--lg-error-bg); border: 1px solid var(--lg-error-border);
          border-radius: 10px;
        }

        .login-btn {
          width: 100%; background: var(--lg-brand); color: #fff;
          border: none; padding: 13px; border-radius: 10px;
          font-size: 15px; font-weight: 600; font-family: 'IBM Plex Sans', sans-serif;
          cursor: pointer; transition: opacity 0.15s;
        }
        .login-btn:hover { opacity: 0.92; }
        .login-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .login-divider {
          margin-top: 24px; padding-top: 20px;
          border-top: 1px solid var(--lg-border);
          text-align: center;
        }
        .login-divider-text { font-size: 13.5px; color: var(--lg-text-faint); }
        .login-divider a { font-size: 13.5px; color: var(--lg-brand); font-weight: 600; text-decoration: none; }
        .login-divider a:hover { text-decoration: underline; }

        .login-bottom {
          display: flex; align-items: center; justify-content: center;
          gap: 20px; margin-top: 28px;
        }
        .login-bottom a { font-size: 13px; color: var(--lg-text-faint); text-decoration: none; transition: color 0.15s; }
        .login-bottom a:hover { color: var(--lg-text-muted); }

        .cgu-scroll {
          max-height: 260px; overflow-y: auto; padding: 16px;
          background: var(--lg-bg); border: 1px solid var(--lg-border); border-radius: 10px;
          margin-bottom: 18px;
        }
        .cgu-scroll h3 { font-size: 12.5px; font-weight: 600; color: var(--lg-text); margin: 14px 0 4px; }
        .cgu-scroll h3:first-child { margin-top: 0; }
        .cgu-scroll p { font-size: 12.5px; color: var(--lg-text-muted); line-height: 1.7; margin: 0; }
        .login-btn.accepte { background: #2F7D4F; }

        @media (max-width: 480px) {
          .login-nav { padding: 20px; }
          .login-bottom { flex-wrap: wrap; gap: 16px; }
        }
      `}</style>

      <div className="login-page" data-login-theme={theme}>
        <nav className="login-nav">
          <a className="login-nav-link" href="https://ster-ai.com">
            <span>←</span> Retour au site
          </a>
          <button
            className="login-theme-toggle"
            aria-label="Changer de thème"
            onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
          >
            {theme === 'dark' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3v2M12 19v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M3 12h2M19 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><circle cx="12" cy="12" r="4"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.7 15.3a8.5 8.5 0 1 1-10-10 7 7 0 0 0 10 10z"/></svg>
            )}
          </button>
        </nav>

        <div className="login-main">
          <div className="login-card-wrap">
            <div className="login-logo-center">
              <div className="login-logo-dot"></div>
              <div className="login-logo-text">Ster-AI</div>
            </div>

            <div className="login-card">
              {etape === 'identifiants' ? (
                <>
                  <div className="login-title">Connexion</div>
                  <div className="login-sub">Accédez à votre espace de travail</div>

                  <form onSubmit={handleSubmit}>
                    <div className="login-field">
                      <label className="login-label">Email</label>
                      <input
                        className="login-input"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="vous@organisation.fr"
                        autoComplete="email"
                        autoFocus
                      />
                    </div>

                    <div className="login-field">
                      <label className="login-label">Mot de passe</label>
                      <input
                        className="login-input"
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="••••••••"
                        autoComplete="current-password"
                      />
                    </div>

                    {erreur && (
                      <div className="login-erreur">
                        <span>⚠</span> {erreur}
                      </div>
                    )}

                    <button className="login-btn" type="submit" disabled={loading}>
                      {loading ? 'Connexion...' : 'Se connecter'}
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <div className="login-title">Vérification</div>
                  <div className="login-sub">Un code de connexion vient d'être envoyé à {email}</div>

                  <form onSubmit={handleCodeSubmit}>
                    <div className="login-field">
                      <label className="login-label">Code à 6 chiffres</label>
                      <input
                        className="login-input"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={code}
                        onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="000000"
                        autoComplete="one-time-code"
                        autoFocus
                      />
                    </div>

                    {erreur && (
                      <div className="login-erreur">
                        <span>⚠</span> {erreur}
                      </div>
                    )}

                    <button className="login-btn" type="submit" disabled={loading || code.length !== 6}>
                      {loading ? 'Vérification...' : 'Valider'}
                    </button>
                  </form>

                  <div className="login-divider">
                    <a href="#" onClick={e => { e.preventDefault(); setEtape('identifiants'); setCode(''); setErreur(''); }}>← Revenir en arrière</a>
                  </div>
                </>
              )}

              {etape === 'cgu' && (
                <>
                  <div className="login-title">Avant de continuer</div>
                  <div className="login-sub">Merci de lire et d'accepter les Conditions Générales d'Utilisation</div>

                  <div className="cgu-scroll" ref={cguRef} onScroll={handleScrollCgu}>
                    <h3>1. Objet</h3>
                    <p>Les présentes CGU régissent l'utilisation de la plateforme Ster-AI, service d'automatisation documentaire par intelligence artificielle.</p>
                    <h3>2. Accès au service</h3>
                    <p>L'accès à Ster-AI est réservé aux clients disposant d'identifiants valides. Toute tentative d'accès non autorisé est interdite.</p>
                    <h3>3. Responsabilité</h3>
                    <p>Les analyses produites par Ster-AI sont fournies à titre indicatif. L'utilisateur reste seul responsable de l'utilisation des résultats.</p>
                    <h3>4. Données</h3>
                    <p>Les documents déposés sur la plateforme sont traités uniquement pour produire les analyses demandées. Ils ne sont pas conservés au-delà de la session d'analyse.</p>
                    <h3>5. Propriété intellectuelle</h3>
                    <p>La plateforme Ster-AI est la propriété exclusive de Louis Lhertere. Dépôt eSoleau INPI effectué le 13/05/2026.</p>
                    <h3>6. Tarification</h3>
                    <p>Les conditions tarifaires sont définies dans les Conditions Générales de Vente et, le cas échéant, dans le contrat signé entre Ster-AI et le client.</p>
                    <h3>7. Connexion à des comptes tiers</h3>
                    <p>Certaines fonctionnalités permettent de connecter Ster-AI à un compte de messagerie ou de stockage du client (via autorisation OAuth Google ou Microsoft). Cette connexion est explicite, limitée aux seules permissions nécessaires, et révocable à tout moment par le client depuis son propre compte. Ster-AI ne stocke pas les documents sources récupérés par ce biais au-delà du traitement nécessaire à l'analyse.</p>
                    <h3>8. Droit applicable</h3>
                    <p>Les présentes CGU sont soumises au droit français.</p>
                  </div>

                  <button
                    className={`login-btn ${cguDefile ? 'accepte' : ''}`}
                    disabled={!cguDefile || loading}
                    onClick={handleAccepterCgu}
                  >
                    {loading ? 'Un instant...' : cguDefile ? "J'ai lu et j'accepte les CGU" : 'Faites défiler jusqu\'en bas pour accepter'}
                  </button>

                  <div className="login-divider">
                    <a href="https://ster-ai.com/#cgu" target="_blank" rel="noopener noreferrer">Voir les CGU en plein écran ↗</a>
                  </div>
                </>
              )}

              {etape === 'contrat' && contratStructure && (
                <>
                  <div className="login-title">Contrat de prestation</div>
                  <div className="login-sub">Merci de lire et d'accepter votre contrat avant de continuer</div>

                  <div className="cgu-scroll" ref={contratRef} onScroll={handleScrollContrat}>
                    {contratStructure.sections.map((sec, i) => (
                      <div key={i}>
                        <h3>{sec.titre}</h3>
                        {sec.contenu.split('\n').map((ligne, j) => ligne.trim() ? <p key={j}>{ligne}</p> : null)}
                      </div>
                    ))}
                  </div>

                  <button
                    className={`login-btn ${contratDefile ? 'accepte' : ''}`}
                    disabled={!contratDefile || loading}
                    onClick={handleAccepterContrat}
                  >
                    {loading ? 'Un instant...' : contratDefile ? "J'ai lu et j'accepte le contrat" : 'Faites défiler jusqu\'en bas pour accepter'}
                  </button>
                </>
              )}

              <div className="login-divider">
                <span className="login-divider-text">Besoin d'aide ? </span>
                <a href="mailto:contact@ster-ai.com">contact@ster-ai.com</a>
              </div>
            </div>

            <div className="login-bottom">
              <a href="https://ster-ai.com">ster-ai.com</a>
              <a href="mailto:contact@ster-ai.com">Contact</a>
              <a href="https://ster-ai.com/#offres">Offres</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
